import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Settings, Wallet, Gauge, Search, Check, ChevronDown } from 'lucide-react';
import InputLabel from '../../../components/base/InputLabel';
import NumberInput from '../../../components/base/NumberInput';
import Parameter from '../../../components/base/Parameter';
import { ERROR_MESSAGES } from '../../../errors/constants';
import { MIN_VALUES } from '../../../constants/minValues';
import { FOREX_PAIRS } from '../config/pairs.ts';

// Pair Selector chip — portal dropdown with search (same pattern as ContractSelectorChip)
const PairSelectorChip = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4 + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) setSearchQuery('');
    setIsOpen(!isOpen);
  };

  const handleSelect = (symbol) => {
    onChange(symbol);
    setIsOpen(false);
  };

  const filteredPairs = Object.entries(FOREX_PAIRS).filter(([symbol, spec]) => {
    const query = searchQuery.toLowerCase();
    return symbol.toLowerCase().includes(query) || spec.name.toLowerCase().includes(query);
  });

  const dropdownMenu = isOpen && createPortal(
    <div
      ref={menuRef}
      className="fixed z-[9999] w-[360px]"
      style={{
        top: menuPosition.top,
        left: menuPosition.left,
        animation: 'dropdownIn 150ms ease-out'
      }}
    >
      <div className="bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden">
        <div className="p-3 border-b border-white/5">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search pairs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg pl-10 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50"
            />
          </div>
        </div>

        <div className="py-2 max-h-[320px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
          {filteredPairs.map(([symbol, spec]) => (
            <div
              key={symbol}
              onClick={() => handleSelect(symbol)}
              className={`px-3 py-2.5 mx-2 rounded-lg cursor-pointer hover:bg-white/10 transition-colors flex items-center justify-between ${value === symbol ? 'bg-white/5' : ''}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-amber-400 font-mono font-bold w-20">{symbol}</span>
                <div>
                  <div className="text-slate-300 text-sm">{spec.name}</div>
                  <div className="text-slate-500 text-xs font-mono">
                    Pip: {spec.pipSize} | {spec.decimals} decimals
                  </div>
                </div>
              </div>
              {value === symbol && (
                <Check size={20} className="text-emerald-400 flex-shrink-0" />
              )}
            </div>
          ))}
          {filteredPairs.length === 0 && (
            <div className="px-4 py-3 text-slate-500 text-sm text-center">
              No pairs found
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="h-10 px-4 w-full lg:w-auto bg-slate-800 rounded-lg flex items-center justify-between lg:justify-start gap-2 hover:bg-slate-700 transition-colors border border-transparent hover:border-slate-600"
      >
        <span className="text-slate-300 text-sm">{value}</span>
        <ChevronDown
          size={14}
          className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {dropdownMenu}
    </>
  );
};

const ForexHeader = ({ values, setValues, pairSpec, onOpenSettings, errors = {}, extremePriceField }) => {
  const handlePairChange = (symbol) => {
    setValues(prev => ({ ...prev, pair: symbol }));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col lg:flex-row gap-4 lg:gap-6 lg:items-start">
      <div className="grid grid-cols-2 gap-3 lg:contents">
        <div className="w-full lg:w-72">
          <div className="flex justify-between items-center mb-2">
            <InputLabel variant="header" required>Entry price</InputLabel>
          </div>
          <NumberInput
            value={values.entryPrice}
            onChange={(value) => setValues(prev => ({ ...prev, entryPrice: value }))}
            decimals={pairSpec.decimals}
            required
            min={MIN_VALUES.entryPrice}
            name="entryPrice"
            error={errors.entryPrice ? { message: ERROR_MESSAGES.INVALID_ENTRY_PRICE } : null}
            highlight={extremePriceField === 'entryPrice'}
            copyEnabled={true}
          />
        </div>

        <div className="w-full lg:w-72">
          <div className="flex justify-between items-center mb-2">
            <InputLabel variant="header" required>Stop-loss</InputLabel>
          </div>
          <NumberInput
            value={values.stopLoss}
            onChange={(value) => setValues(prev => ({ ...prev, stopLoss: value }))}
            decimals={pairSpec.decimals}
            required
            min={MIN_VALUES.stopLoss}
            name="stopLoss"
            error={errors.stopLoss ? { message: ERROR_MESSAGES.INVALID_STOP_LOSS } : null}
            highlight={extremePriceField === 'stopLoss'}
            copyEnabled={true}
          />
        </div>
      </div>

      <div className="flex-1">
        <div className="flex justify-between items-center mb-2">
          <InputLabel variant="header">Parameters</InputLabel>
          <button
            onClick={onOpenSettings}
            className="flex items-center gap-1.5 text-slate-500 text-sm hover:text-slate-400 transition-colors tracking-wide"
          >
            Settings <Settings size={14} />
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex gap-2 lg:gap-3 mt-[6px]">
          <div className="col-span-2 sm:col-span-3 lg:col-auto">
            <PairSelectorChip
              value={values.pair}
              onChange={handlePairChange}
            />
          </div>
          <Parameter
            value={`${values.maxLeverage}x`}
            onChange={setValues}
            isDirectEditable={true}
          >
            <Gauge size={16} className="text-slate-500 mr-2" />
          </Parameter>
          <Parameter
            label="Sprd"
            value={`${values.spread}p`}
            onChange={setValues}
            isDirectEditable={true}
          />
          <Parameter
            label="Risk"
            value={values.riskUnit === 'percent' ? `${values.risk}%` : `$${values.risk}`}
            onChange={setValues}
            isDirectEditable={true}
          />
          <Parameter
            label={values.multiTpEnabled ? "TPs" : "R:R"}
            value={`${values.nominalRR}x`}
            onChange={setValues}
            isDirectEditable={true}
            staticDisplay={values.multiTpEnabled ? String(values.tpCount) : undefined}
            onOpenSettings={onOpenSettings}
          />
          <Parameter
            icon={Wallet}
            value={`$${values.accountBalance}`}
            onChange={setValues}
            onOpenSettings={onOpenSettings}
          />
        </div>
      </div>
    </div>
  );
};

export default ForexHeader;
